/* eslint-disable operator-linebreak */
import Splide from '@splidejs/splide';

class Slider {
	currentIndex = 0;

	constructor(el) {
		if (el) {
			this.el = el;
			this.track = this.el.querySelector('.splide__track');
			this.slideCount = this.el.querySelectorAll('.splide__slide').length;
			this.splide = new Splide(this.el);
			this.slides = this.el.querySelectorAll('.splide__slide');
			this.arrowsEl = this.el.querySelector('.splide__arrows');
			this.scrollbar = this.el.querySelector('.em-slider__scrollbar');
			this.scrollbarThumb = this.el.querySelector(
				'.em-slider__scrollbar-thumb',
			);
			this.options = JSON.parse(this.el.getAttribute('data-splide'));

			if (this.options.syncId) {
				this.syncedSplide = new Splide(
					`#${this.options.syncId}`,
					this.options.syncOptions || {},
				);
				this.splide.sync(this.syncedSplide);
			}

			this.splide.mount();

			if (this.syncedSplide) {
				this.syncedSplide.mount();
			}

			if (this.scrollbar && this.scrollbarThumb) {
				this.initScrollbar();
				window.addEventListener('resize', this.initScrollbar);
			}

			this.updateTrackHeight();
			window.addEventListener('resize', () => {
				window.requestAnimationFrame(() => {
					this.updateTrackHeight();
				});
			});

			this.maybeHideArrows();
			window.addEventListener('resize', this.maybeHideArrows);

			this.splide.on('move', this.beforeMove);
			this.splide.on('moved', this.afterMove);
		}
	}

	isVertical = () => this.splide._options.direction === 'ttb';

	getTallestSlideHeight = () => {
		let height = 0;
		this.slides.forEach((slide) => {
			if (slide.offsetHeight > height) {
				height = slide.offsetHeight;
			}
		});
		return height;
	};

	updateTrackHeight = () => {
		if (
			this.slides[this.currentIndex] &&
			this.isVertical() &&
			this.options.heightCssVar
		) {
			document.documentElement.style.setProperty(
				this.options.heightCssVar,
				`${this.slides[this.currentIndex].offsetHeight}px`,
			);
		}
	};

	beforeMove = (newIndex, prevIndex) => {
		let enteringSlide;
		let leavingSlide;

		if (newIndex > prevIndex) {
			enteringSlide = this.el.querySelector(
				'.splide__slide.is-visible + .splide__slide:not(.is-visible)',
			);
			leavingSlide = this.el.querySelector('.splide__slide.is-active');
		} else {
			enteringSlide = this.el.querySelector('.splide__slide.is-prev');
			leavingSlide =
				this.el.querySelector(
					'.splide__slide.is-visible + .splide__slide:not(.is-visible)',
				)?.previousElementSibling ??
				this.el.querySelector('.splide__slide.is-visible:last-child');
		}

		if (enteringSlide) {
			enteringSlide.classList.add('is-entering');
		}

		if (leavingSlide) {
			leavingSlide.classList.add('is-leaving');
		}

		this.currentIndex = newIndex;
		this.updateTrackHeight();
	};

	afterMove = () => {
		const slides = this.el.querySelectorAll('.is-entering, .is-leaving');
		if (slides.length) {
			slides.forEach((slide) => {
				slide.classList.remove('is-entering');
				slide.classList.remove('is-leaving');
			});
		}
	};

	maybeHideArrows = () => {
		window.requestAnimationFrame(() => {
			const splideData = this.el.getAttribute('data-splide');
			const parsedData = JSON.parse(splideData);
			if (parsedData && parsedData.breakpoints) {
				let breakpointKeys = Object.keys(parsedData.breakpoints);
				breakpointKeys = breakpointKeys.map((key) => parseInt(key));
				breakpointKeys = breakpointKeys.sort((a, b) => {
					if (a > b) return 1;
					if (a < b) return -1;
					return 0;
				});

				let currentBreakpoint = false;
				breakpointKeys.every((bp) => {
					if (bp >= window.innerWidth) {
						currentBreakpoint = bp;
						return false;
					}
					return true;
				});
				let bpPerPage = parsedData.perPage;
				if (currentBreakpoint) {
					bpPerPage = parsedData.breakpoints[currentBreakpoint].perPage;
				}
				if (bpPerPage && this.slideCount <= bpPerPage) {
					this.arrowsEl.style.display = 'none';
				} else {
					this.arrowsEl.style.display = '';
				}
			}
		});
	};

	/**
	 * Get the current perPage value from a Splide slider, considering responsive settings.
	 * @return {number} - The current perPage value.
	 */
	getCurrentPerPage = () => {
		// Get the current screen width
		const screenWidth = window.innerWidth;

		// Start with the default perPage value
		let currentPerPage = this.splide.options.perPage;

		// Check if there are responsive settings
		if (this.splide.options.breakpoints) {
			// Iterate over the breakpoints in descending order
			const breakpoints = Object.keys(this.splide.options.breakpoints).sort(
				(a, b) => b - a,
			);
			// Loop through the sorted breakpoints
			for (let i = 0; i < breakpoints.length; i++) {
				const breakpoint = breakpoints[i];
				if (screenWidth >= breakpoint) {
					// Update currentPerPage with the value from the matching breakpoint
					if (
						this.splide.options.breakpoints[breakpoint].perPage !== undefined
					) {
						currentPerPage =
							this.splide.options.breakpoints[breakpoint].perPage;
					}
					break;
				}
			}
		}

		return parseInt(currentPerPage);
	};

	initScrollbar = () => {
		const trackWidth = this.scrollbar.offsetWidth;
		const numSlides = this.splide.length;
		const thumbWidth = (trackWidth * this.getCurrentPerPage()) / numSlides;
		this.scrollbarThumb.style.width = `${thumbWidth}px`;

		let isDragging = false;
		let currentSlideIndex = 0;

		this.scrollbarThumb.addEventListener('mousedown', (e) => {
			isDragging = true;
			document.body.classList.add('no-transition'); // Optionally disable transitions while dragging
			const startX = e.clientX - this.scrollbarThumb.offsetLeft;

			const thumbMove = (e) => {
				const newX = e.clientX - startX;
				const maxX = trackWidth - thumbWidth;
				const newPosition = Math.max(0, Math.min(newX, maxX));
				this.scrollbarThumb.style.left = `${newPosition}px`;
				currentSlideIndex = Math.round(
					(newPosition / maxX) * (numSlides - this.getCurrentPerPage()),
				);
				this.splide.go(currentSlideIndex);
			};

			const stopDragging = () => {
				isDragging = false;
				document.removeEventListener('mousemove', thumbMove);
				document.removeEventListener('mouseup', stopDragging);
				document.body.classList.remove('no-transition'); // Re-enable transitions
			};

			document.addEventListener('mousemove', thumbMove);
			document.addEventListener('mouseup', stopDragging);
		});

		this.splide.on('moved', () => {
			if (!isDragging) {
				currentSlideIndex = this.splide.index;
				const maxX = trackWidth - thumbWidth;
				const newPosition =
					(currentSlideIndex / (numSlides - this.getCurrentPerPage())) * maxX;
				this.scrollbarThumb.style.left = `${newPosition}px`;
			}
		});
	};
}

const sliderEls = [...document.querySelectorAll('.splide[data-splide]')];
if (sliderEls.length) {
	embark.components.splides = sliderEls.map((el) => new Slider(el));
	window.dispatchEvent(
		new CustomEvent('em-slider-mounted', {
			bubbles: true,
			detail: { text: () => Date.now() },
		}),
	);
}
